// Anti-spam helpers para formulários (sem backend de rate-limit).
// Camadas: honeypot, tempo mínimo de preenchimento, cooldown e dedupe por escopo.

const STORAGE_PREFIX = "kentro_antispam_v1:";
const COOLDOWN_MS = 30_000; // 30s entre envios bem-sucedidos do mesmo formulário
const DEDUPE_WINDOW_MS = 5 * 60_000; // 5 min para bloquear conteúdo idêntico
const MIN_FILL_MS = 2_500; // tempo mínimo entre abrir e enviar

type Stored = { lastAt: number; lastHash: string };

function read(scope: string): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + scope);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

function write(scope: string, s: Stored) {
  try {
    localStorage.setItem(STORAGE_PREFIX + scope, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

async function hashContent(input: string): Promise<string> {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(input));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
  return String(h);
}

export type AntispamCheck =
  | { ok: true; hash: string }
  | { ok: false; reason: "honeypot" | "too_fast" | "cooldown" | "duplicate"; retryInSec?: number };

export async function checkAntispam(opts: {
  scope: string;
  honeypot: string;
  formOpenedAt: number;
  contentKey: string;
  cooldownMs?: number;
  minFillMs?: number;
}): Promise<AntispamCheck> {
  const cooldownMs = opts.cooldownMs ?? COOLDOWN_MS;
  const minFillMs = opts.minFillMs ?? MIN_FILL_MS;

  if (opts.honeypot.trim().length > 0) return { ok: false, reason: "honeypot" };

  const elapsed = Date.now() - opts.formOpenedAt;
  if (elapsed < minFillMs) return { ok: false, reason: "too_fast" };

  const hash = await hashContent(opts.contentKey);
  const stored = read(opts.scope);
  const now = Date.now();
  if (stored) {
    if (now - stored.lastAt < cooldownMs) {
      return {
        ok: false,
        reason: "cooldown",
        retryInSec: Math.ceil((cooldownMs - (now - stored.lastAt)) / 1000),
      };
    }
    if (stored.lastHash === hash && now - stored.lastAt < DEDUPE_WINDOW_MS) {
      return { ok: false, reason: "duplicate" };
    }
  }
  return { ok: true, hash };
}

export function markAntispamSubmitted(scope: string, hash: string) {
  write(scope, { lastAt: Date.now(), lastHash: hash });
}

/** Mensagem padrão (PT) para exibir em toast quando bloqueado. */
export function antispamMessagePt(c: AntispamCheck): string | null {
  if (c.ok) return null;
  if (c.reason === "cooldown") return `Aguarde ${c.retryInSec ?? 30}s antes de enviar novamente.`;
  if (c.reason === "duplicate") return "Este envio já foi registrado recentemente.";
  return "Envio bloqueado por proteção anti-spam. Tente novamente.";
}

/* ------------------------------------------------------------------ *
 * Camada 2: sinais de interação humana + heurísticas de conteúdo
 * ------------------------------------------------------------------ */

export type HumanSignals = {
  /** Eventos de teclado reais (keydown com isTrusted). */
  keys: number;
  /** Movimentos de mouse / toques / eventos de ponteiro reais. */
  pointer: number;
  /** true quando há evidência mínima de um humano operando a página. */
  ok: boolean;
};

/**
 * Observa eventos confiáveis (isTrusted) na janela para distinguir
 * preenchimento humano de automação (Playwright/Selenium/cURL + JS).
 * Retorna um leitor + função de cleanup.
 */
export function createHumanSignalTracker() {
  let keys = 0;
  let pointer = 0;
  const onKey = (e: Event) => {
    if (e.isTrusted) keys++;
  };
  const onPointer = (e: Event) => {
    if (e.isTrusted) pointer++;
  };
  if (typeof window !== "undefined") {
    window.addEventListener("keydown", onKey, { passive: true });
    window.addEventListener("pointerdown", onPointer, { passive: true });
    window.addEventListener("pointermove", onPointer, { passive: true });
    window.addEventListener("touchstart", onPointer, { passive: true });
    window.addEventListener("wheel", onPointer, { passive: true });
  }
  return {
    read(): HumanSignals {
      return { keys, pointer, ok: keys + pointer >= 3 };
    },
    dispose() {
      if (typeof window === "undefined") return;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("touchstart", onPointer);
      window.removeEventListener("wheel", onPointer);
    },
  };
}

/** Indícios de navegador automatizado (headless / webdriver). */
export function looksAutomated(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { webdriver?: boolean };
  if (nav.webdriver === true) return true;
  const ua = navigator.userAgent || "";
  return /headlesschrome|phantomjs|puppeteer|playwright|selenium|bot|crawler|spider/i.test(ua);
}

const DISPOSABLE_EMAIL_DOMAINS = [
  "mailinator.com",
  "yopmail.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  "temp-mail.org",
  "trashmail.com",
  "sharklasers.com",
  "getnada.com",
  "dispostable.com",
  "fakeinbox.com",
  "maildrop.cc",
  "throwawaymail.com",
  "mailnesia.com",
  "example.com",
  "test.com",
];

const VOWELS = /[aeiouáéíóúãõâêôàü]/i;

/** Nome plausível: 2+ caracteres, tem vogal, não é sequência de teclado/repetição. */
function plausibleName(name: string): boolean {
  const v = name.trim();
  if (v.length < 2 || v.length > 120) return false;
  if (!VOWELS.test(v)) return false;
  if (/^(.)\1+$/.test(v.replace(/\s+/g, ""))) return false;
  if (/(qwer|asdf|zxcv|1234|test|teste|abcd)/i.test(v)) return false;
  if (/https?:\/\/|<[a-z]|\[url|\{\{/i.test(v)) return false;
  return true;
}

export type ContentCheck = { ok: true } | { ok: false; field: "name" | "email" | "phone" | "message" };

/**
 * Heurística de conteúdo: bloqueia lixo típico de bots (nomes aleatórios,
 * e-mails descartáveis, telefones repetidos, spam com links na mensagem).
 */
export function checkContentQuality(input: {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}): ContentCheck {
  if (input.name !== undefined && !plausibleName(input.name)) return { ok: false, field: "name" };

  if (input.email) {
    const email = input.email.trim().toLowerCase();
    const domain = email.split("@")[1] ?? "";
    if (!domain.includes(".")) return { ok: false, field: "email" };
    if (DISPOSABLE_EMAIL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`)))
      return { ok: false, field: "email" };
  }

  if (input.phone) {
    const digits = input.phone.replace(/\D/g, "");
    // Aceita números internacionais (nacionais curtos, ex.: Portugal/Espanha) além do padrão BR.
    if (digits.length < 6 || digits.length > 15) return { ok: false, field: "phone" };
    if (/^(\d)\1+$/.test(digits)) return { ok: false, field: "phone" };
    if (/^0+$/.test(digits) || digits.startsWith("0000")) return { ok: false, field: "phone" };
    if (/(0123456789|1234567890|9876543210)/.test(digits)) return { ok: false, field: "phone" };
  }

  if (input.message) {
    const m = input.message;
    const links = (m.match(/https?:\/\//gi) ?? []).length;
    if (links >= 2) return { ok: false, field: "message" };
    if (/\[url=|<a\s+href|viagra|casino|bitcoin\s+invest|seo\s+services/i.test(m))
      return { ok: false, field: "message" };
  }

  return { ok: true };
}

/** Mensagem amigável (PT) para falha de qualidade de conteúdo. */
export function contentMessagePt(c: ContentCheck): string | null {
  if (c.ok) return null;
  switch (c.field) {
    case "name":
      return "Informe seu nome completo real para continuarmos.";
    case "email":
      return "Use um e-mail válido e permanente (e-mails temporários não são aceitos).";
    case "phone":
      return "Informe um telefone válido (com DDD ou código do país).";
    default:
      return "Sua mensagem foi identificada como possível spam. Reescreva sem links.";
  }
}
